import React, { useState } from "react";
import { Form } from "react-bootstrap";
import "./AddEmailSidebar.css";
import { ChevronDown } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { manageAgentSessionIds } from "../../helpers";
import { generateEmail } from "../../services/email";
import EmailDetailsContainer from "./EmailDetailsContainer";
import { EmailSectionProvider } from "../../components/Context/EmailSectionContext";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AddEmailSidebar = ({
  emailData,
  templateName,
  templateId: receivedTemplateId,
  previewImage,
  setParentLoading,
  onEmailChunk,
  onStartStreaming,
  isLoading,
  setIsLoading,
  sku,
  furtherDetails,
  setFurtherDetails,
  tone,
  setTone,
  occasion,
  setOccasion
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialSku = sku || location.state?.sku || "";
  const formattedSku = initialSku.trim().replace(/\s+/g, "_").toLowerCase();
  const [keywords, setKeywords] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState(null);
  const { agentId, sessionId } = manageAgentSessionIds(initialSku);
  
  // Extract sections properly to handle arrays, objects with sections, or objects with component_name
  const extractSections = (data) => {
    if (!data) return [];
    // If data is already an array, return it directly
    if (Array.isArray(data)) return data;
    // If data has a non-empty sections array, use it
    if (Array.isArray(data.sections) && data.sections.length > 0) return data.sections;
    // Otherwise, extract valid section objects with component_name
    return Object.values(data).filter(
      section => section && 
        typeof section === 'object' && 
        section.component_name && 
        Object.keys(section).length > 0
    );
  };
  
  // Use the extract function on initial data
  const [initialSections] = useState(() => 
    extractSections(location.state?.emailData || emailData)
  );

  const handleOptimize = async () => {
    if (onStartStreaming) onStartStreaming();
    setIsLoading(true);
    setError(null);
    setParentLoading?.(true);
    
    const sku = formattedSku;

    const payload = {
      preview_image: previewImage,
      request_type: "email",
      additional_data: {
        agent_id: "agentId" + Math.floor(Math.random() * 1000000),
        session_id: "sessionId" + Math.floor(Math.random() * 1000000),
        template: templateName,
        occasion: occasion,
        tone: tone,
        further_details: furtherDetails,
        streaming: true,
      },
    };

    let accumulatedData = [];

    const onDataChunk = (chunk) => {
      if (onEmailChunk) {
        setIsLoading(false);
        onEmailChunk(chunk);
      }
      accumulatedData.push(chunk);
    };

    const onComplete = () => {
      setIsLoading(false);
      setParentLoading?.(false);
      toast.success("Email generated successfully!");
      navigate("/skills/email/edit-email", {
        state: {
          emailData: accumulatedData,
          templateName,
          templateId: receivedTemplateId,
          previewImage,
          tone,
          furtherDetails,
          occasion,
        },
      });
    };

    const onError = (err) => {
      const errorMessage = err.message || "Failed to optimize email";
      setError(errorMessage);
      setIsLoading(false);
      setParentLoading?.(false);
      toast.error(errorMessage);
    };

    try {
      await generateEmail(payload, onDataChunk, onComplete, onError);
    } catch (error) {
      const errorMessage = error.message || "Failed to generate email";
      setError(errorMessage);
      setIsLoading(false);
      setParentLoading?.(false);
      toast.error(errorMessage);
    }
  };

  const handleKeyDown = (e) => {
    if (["Enter", ","].includes(e.key)) {
      e.preventDefault();
      const value = inputValue.trim();
      if (value && !keywords.includes(value)) {
        setKeywords([...keywords, value]);
        setInputValue("");
      }
    }
  };

  const removeKeyword = (index) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  return (
    <div className="edit-product-left1">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <h2 className="form-title">Please fill in your information</h2>

      <Form>
        <div className="mainform edit-product-left">
          <div className="form-section ">
            <label className="form-label">
              What the occasion this is for
            </label>
            <Form.Control
              type="text"
              placeholder="e.g. Welcome a new customer"
              className="form-input"
              value={occasion}
              onChange={(e) => setOccasion(e.target.value)}
            />
          </div>

          <div className="form-section">
            <label className="form-label">
              Include further details here (Offers,Offer names,Specific sections)
            </label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Add details about the product"
              className="form-textarea"
              value={furtherDetails}
              onChange={(e) => setFurtherDetails(e.target.value)}
            />
          </div>

          <div className="form-section">
            <label className="form-label">
              What should be the tone of voice?
            </label>
            <div className="custom-dropdown">
              <Form.Control as="select" className="form-input"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
              >
                <option value="" disabled>
                  e.g. formal
                </option>
                <option value="formal">Formal</option>
                <option value="informal">Informal</option>
                <option value="friendly">Friendly</option>
                <option value="casual">Casual</option>
                <option value="professional">Professional</option>
                <option value="persuasive">Persuasive</option>
              </Form.Control>
              <ChevronDown className="dropdown-icon" />
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message mt-2 mb-2">
            {error}
          </div>
        )}

        <button
          type="button"
          className="optimize-button mt-2"
          onClick={handleOptimize}
          disabled={isLoading}
        >
          Generate optimized email
        </button>

        <button type="button" className="documents-button">
          Add additional documents
        </button>
      </Form>
    </div>
  );
};

export default AddEmailSidebar;
